import type { MaterialSrc } from "common/materials/materialSrc";
import { materialCacheKey } from "common/materials/materialSrc";
import TaskQueue from "common/utils/taskQueue";
import type { GLTFDesignMaterialParamsByKey } from "gfx/gltf/gltfBuildingExtras";
import { HTTPMaterialParameterLoader } from "gfx/HTTPMaterialParameterLoader";
import type { MaterialFactory } from "gfx/MaterialFactory";

const MATERIAL_WARMUP_CONCURRENCY = 4;

export interface ShowroomMaterialWarmupManifest {
  readonly sessionId: string;
  readonly materialSources: readonly MaterialSrc[];
  readonly designMaterialParamsByKey: GLTFDesignMaterialParamsByKey;
  /**
   * False for the worker's early (post-generation, palette-derived) manifest; true for the
   * post-collection manifest. The pre-render compile warmup that consumes this distinction
   * lands in the compile-warmup change stacked above; here both manifests drive the same
   * full warmup.
   */
  readonly final: boolean;
}

let currentSessionId: string | null = null;
let latestManifest: ShowroomMaterialWarmupManifest | null = null;
let registeredMaterialFactory: MaterialFactory | null = null;
let activeWarmupId = 0;

function primeDesignMaterialParams(designMaterialParamsByKey: GLTFDesignMaterialParamsByKey): void {
  const materialLoader = HTTPMaterialParameterLoader.instance;
  for (const entry of Object.values(designMaterialParamsByKey)) {
    if (entry.materialSrc && entry.params) {
      materialLoader.primeDesignMaterialParams(entry.materialSrc, entry.params);
    }
  }
}

function collectUniqueMaterialSources(materialSources: readonly MaterialSrc[]): MaterialSrc[] {
  const materialSourcesByKey = new Map<string, MaterialSrc>();
  for (const materialSrc of materialSources) {
    const key = materialCacheKey(materialSrc);
    if (!materialSourcesByKey.has(key)) {
      materialSourcesByKey.set(key, materialSrc);
    }
  }
  return Array.from(materialSourcesByKey.values());
}

async function warmMaterials(
  manifest: ShowroomMaterialWarmupManifest,
  materialFactory: MaterialFactory,
  warmupId: number,
): Promise<void> {
  const queue = new TaskQueue<void>(MATERIAL_WARMUP_CONCURRENCY);
  const materialSources = collectUniqueMaterialSources(manifest.materialSources);
  await Promise.all(
    materialSources.map((materialSrc) =>
      queue.enqueue(async () => {
        if (warmupId !== activeWarmupId || manifest.sessionId !== currentSessionId) {
          return;
        }
        if (materialFactory.designMaterialIsFetched(materialSrc)) {
          return;
        }
        await materialFactory.getDesignMaterial(materialSrc);
      }),
    ),
  );
}

function startLatestWarmup(): void {
  if (!latestManifest || !registeredMaterialFactory) {
    return;
  }

  activeWarmupId += 1;
  const warmupId = activeWarmupId;
  const manifest = latestManifest;
  const materialFactory = registeredMaterialFactory;
  void warmMaterials(manifest, materialFactory, warmupId).catch((error: unknown) => {
    console.warn("Showroom material warmup failed", error);
  });
}

export function publishShowroomMaterialWarmup(manifest: ShowroomMaterialWarmupManifest): void {
  currentSessionId = manifest.sessionId;
  latestManifest = manifest;
  primeDesignMaterialParams(manifest.designMaterialParamsByKey);
  startLatestWarmup();
}

export function registerShowroomMaterialWarmupFactory(materialFactory: MaterialFactory): () => void {
  registeredMaterialFactory = materialFactory;
  startLatestWarmup();

  return () => {
    if (registeredMaterialFactory === materialFactory) {
      registeredMaterialFactory = null;
      activeWarmupId += 1;
    }
  };
}

export function resetShowroomMaterialWarmup(sessionId?: string): void {
  if (sessionId && currentSessionId !== sessionId) {
    return;
  }

  currentSessionId = null;
  latestManifest = null;
  activeWarmupId += 1;
}
